import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Grid, TextField, MenuItem, Button, Select, FormControl, InputLabel, Typography } from "@mui/material";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import countries from "country-telephone-data";
import { useTranslation } from "../TranslationContext"; 

const MobileNumberInput = ({ onSubmit }) => {
    const { t, changeLanguage } = useTranslation(); 

    // Prepare country options dynamically
    const countryOptions = countries.allCountries.map((country) => ({
        code: country.iso2.toUpperCase(),
        label: `${country.name} (+${country.dialCode})`,
        dialCode: `+${country.dialCode}`,
    }));

    const [countryCode, setCountryCode] = useState(countryOptions[0].dialCode);

    // Formik Setup
    const formik = useFormik({
        initialValues: { mobileNumber: "" },
        validationSchema: Yup.object({
            mobileNumber: Yup.string()
                .test("is-valid", t("invalidMobile"), (value) => {
                    if (!value) return false;
                    const phoneNumber = parsePhoneNumberFromString(`${countryCode}${value}`);
                    return phoneNumber ? phoneNumber.isValid() : false;
                })
                .required(t("mobileRequired")),
        }),
        onSubmit: (values) => {
            onSubmit(`${countryCode}${values.mobileNumber}`);
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2} mt={2}>
                {/* Language Selector */}
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel shrink>{t("selectLanguage")}</InputLabel>
                        <Select defaultValue="en" onChange={(e) => changeLanguage(e.target.value)}>
                            <MenuItem value="en">English</MenuItem>
                            <MenuItem value="mr">मराठी</MenuItem>
                            <MenuItem value="ar">العربية</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Country Code Dropdown */}
                <Grid item xs={12} sm={3}>
                    <TextField
                        select
                        fullWidth
                        label={t("country")}
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        InputLabelProps={{ shrink: true }} // Prevents label overlap
                    >
                        {countryOptions.map((option) => (
                            <MenuItem key={option.code} value={option.dialCode}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {/* Mobile Number Input */}
                <Grid item xs={12} sm={9}>
                    <TextField
                        fullWidth
                        margin="dense"
                        name="mobileNumber"
                        label={t("mobileNumber")}
                        type="text"
                        value={formik.values.mobileNumber}
                        onChange={(e) => formik.setFieldValue("mobileNumber", e.target.value.replace(/\D/g, ""))}
                        error={formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)}
                        helperText={formik.touched.mobileNumber && formik.errors.mobileNumber}
                        InputLabelProps={{ shrink: true }} // Prevents label overlap
                    />
                </Grid>

                {/* Dynamic Typography for Language Change */}
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", mt: 2 }}>
                        {t("developerInfo")}
                    </Typography>
                </Grid>
            </Grid>

            <Button type="submit" variant="contained" color="primary" style={{ marginTop: "20px" }}>
                {t("submit")}
            </Button>
        </form>
    );
};

export default MobileNumberInput;
